import { useCallback, useEffect, useState } from 'react';
import {
  applyColorScheme,
  COLOR_SCHEME_STORAGE_KEY,
  type ColorSchemePreference,
  type ResolvedColorScheme,
  readColorSchemePreference,
  resolveColorScheme,
  storeColorSchemePreference,
  systemColorScheme,
} from '../lib/color-scheme';
import { useEventListener } from './use-event-listener';

export type UseColorScheme = {
  /** What the user picked. */
  preference: ColorSchemePreference;
  /** What `preference` currently renders as. */
  resolved: ResolvedColorScheme;
  setPreference: (preference: ColorSchemePreference) => void;
};

/**
 * Reads and writes the spark theme's colour scheme, applying it to `<html>` as it changes.
 */
export function useColorScheme(): UseColorScheme {
  const [preference, setPreferenceState] =
    useState<ColorSchemePreference>(readColorSchemePreference);
  const [systemScheme, setSystemScheme] = useState<ResolvedColorScheme>(systemColorScheme);

  useEffect(() => {
    const media = window.matchMedia?.('(prefers-color-scheme: dark)');
    if (!media) {
      return;
    }

    const onChange = () => setSystemScheme(media.matches ? 'dark' : 'light');
    media.addEventListener('change', onChange);
    return () => media.removeEventListener('change', onChange);
  }, []);

  // Another tab, or another instance of this hook in this one.
  useEventListener(window, 'storage', (event) => {
    if (event.key === COLOR_SCHEME_STORAGE_KEY) {
      setPreferenceState(readColorSchemePreference());
    }
  });

  // `systemScheme` is not read here, but a change to it has to re-run this so a `system`
  // preference follows the OS.
  useEffect(() => {
    applyColorScheme(preference);
  }, [preference, systemScheme]);

  const setPreference = useCallback((next: ColorSchemePreference) => {
    storeColorSchemePreference(next);
    setPreferenceState(next);
  }, []);

  return {
    preference,
    resolved: preference === 'system' ? systemScheme : resolveColorScheme(preference),
    setPreference,
  };
}

/**
 * The scheme currently on `<html>`, without writing it.
 *
 * For code that has to mirror the scheme outside CSS — a Monaco theme, a canvas fill. Unlike
 * `useColorScheme` it never calls `applyColorScheme`, so a non-spark host stays untouched.
 */
export function useResolvedColorScheme(): ResolvedColorScheme {
  const read = () =>
    document.documentElement.classList.contains('dark') ? 'dark' : ('light' as ResolvedColorScheme);
  const [resolved, setResolved] = useState<ResolvedColorScheme>(read);

  useEffect(() => {
    const observer = new MutationObserver(() => setResolved(read()));
    observer.observe(document.documentElement, { attributeFilter: ['class'] });
    setResolved(read());
    return () => observer.disconnect();
  }, []);

  return resolved;
}
